/**
 * AssistService — turns a shoot into markers. That is its only job.
 *
 * It measures how sharp each photograph is, decides which are soft relative to
 * the rest of the shoot, and stores the result. It never assigns a bucket, and
 * it never touches a photograph on disk. Everything it produces is a suggestion
 * the photographer is free to ignore.
 *
 * Work is resumable: each frame is committed as it is scored, so closing the tab
 * halfway through a three-thousand-frame shoot costs nothing but the current
 * frame. Reopening picks up exactly where it stopped.
 */

import { laplacianVariance, FOCUS_PX, FOCUS_TAG } from '../domain/sharpness';
import { softFrames, picksForBurst, pickFloor } from '../domain/assist-rules';
import { burstsOf, groupByBurst } from '../domain/bursts';
import type { PixelReader } from '../adapters/pixel-reader';
import type { Embedder, EmbedderStatus, ModelProgress } from '../adapters/embedder';
import type { AssistRepository } from '../repositories';
import type { ChangeSink, Clock, FocusPixels, ThumbnailBytes } from './ports';

export interface AssistProgress {
  projectId: number;
  phase:
    | 'loading'
    | 'scoring'
    | 'grouping'
    | 'measuring'
    | 'finishing'
    | 'done'
    | 'cancelled'
    | 'failed';
  done: number;
  total: number;
  failed: number;
  /** Set once the model reports in, so the interface can say which one ran. */
  device?: EmbedderStatus['device'];
  /** Only present while the model itself is downloading. */
  download?: ModelProgress;
  /**
   * Why this review did not do all it set out to.
   *
   * With phase 'failed' it means nothing was achieved. With phase 'done' it
   * means the model did not run but the sharpness flags stand.
   */
  error?: string;
}

export interface ReviewOptions {
  signal?: { aborted: boolean };
  onProgress?: (progress: AssistProgress) => void;
  batch?: number;
  /**
   * Whether to run the model as well as the sharpness measure.
   *
   * Off by default: sharpness is free and silent, the model is a 15 MB download
   * the photographer has to agree to.
   */
  deep?: boolean;
}

/** How many frames to score before yielding, so the UI stays responsive. */
const CHUNK = 8;

export class AssistService {
  constructor(
    private readonly assist: AssistRepository,
    private readonly pixels: PixelReader,
    private readonly thumbnailBytes: ThumbnailBytes,
    private readonly sink: ChangeSink,
    private readonly clock: Clock,
    private readonly embedder: Embedder | null = null,
    /**
     * Absent in tests that only care about screening, and absent in a browser
     * that cannot decode. Without it a review still groups bursts and still
     * suggests, falling back to the screening number — worse, but not broken.
     */
    private readonly focusPixels: FocusPixels | null = null,
  ) {}

  /** What the UI draws: which frames are soft, which are picks. */
  markers(projectId: number) {
    return this.assist.markersFor(projectId);
  }

  progress(projectId: number): AssistProgress {
    const { total, scored } = this.assist.progress(projectId, FOCUS_TAG);
    return {
      projectId,
      phase: scored >= total && total > 0 ? 'done' : 'scoring',
      done: scored,
      total,
      failed: 0,
    };
  }

  /**
   * True once the shoot has everything a review produces: a vector from the
   * current model on every frame, and — where focus can be measured at all —
   * a focus reading from the current measure too.
   *
   * Both, because a shoot with vectors but no focus readings has bursts whose
   * picks were chosen on the screening number. That is a review that has not
   * finished, and saying otherwise would leave "Find them again" looking like a
   * no-op when it is the thing that would fix the badges.
   */
  isDeepReviewed(projectId: number): boolean {
    if (!this.embedder) return false;
    const { total, embedded, focused } = this.assist.progress(projectId, FOCUS_TAG);
    if (total === 0 || embedded < total) return false;
    return this.focusPixels === null || focused >= total;
  }

  /**
   * Score every unscored frame in a shoot.
   *
   * Returns when the shoot is fully scored or the signal aborts. Safe to call
   * again at any time; already-scored frames are skipped.
   */
  async review(projectId: number, options: ReviewOptions = {}): Promise<AssistProgress> {
    const queue = this.assist.unscored(projectId, options.batch ?? 100_000);
    const { total, scored } = this.assist.progress(projectId);

    const state: AssistProgress = {
      projectId,
      phase: 'scoring',
      done: scored,
      total,
      failed: 0,
    };
    options.onProgress?.({ ...state });

    let read = 0;

    for (let i = 0; i < queue.length; i++) {
      if (options.signal?.aborted) {
        state.phase = 'cancelled';
        options.onProgress?.({ ...state });
        return { ...state };
      }

      const mediaId = queue[i];
      try {
        const bytes = await this.thumbnailBytes(mediaId);
        if (bytes) {
          const score = laplacianVariance(await this.pixels.grayscale(bytes));
          this.assist.putSharpness(mediaId, score, this.clock());
          state.done++;
          read++;
        } else {
          // No thumbnail — the file is missing, undecodable, or its folder has
          // not been reconnected yet. Skipping leaves the frame unscored, so a
          // later pass picks it up once the source is back.
          state.failed++;
        }
      } catch {
        state.failed++;
      }

      // Nothing readable in a whole chunk, and nothing readable so far: the
      // folder is not there. Walking the remaining two thousand frames to fail
      // identically on each wastes minutes and — worse — would end by reporting
      // a completed review that produced nothing at all.
      if (read === 0 && state.failed >= CHUNK) {
        state.phase = 'failed';
        state.error = 'these photos could not be read. Is the folder still connected?';
        options.onProgress?.({ ...state });
        return { ...state };
      }

      // Commit and report in chunks: one write per frame would thrash the
      // debounced snapshot, and one report per frame would thrash React.
      if (i % CHUNK === CHUNK - 1) {
        this.sink.touch();
        options.onProgress?.({ ...state });
        await yieldToUi();
      }
    }

    if (options.deep && this.embedder && !options.signal?.aborted) {
      await this.embed(projectId, state, options);
      if (!options.signal?.aborted) await this.measureFocus(projectId, state, options);
    }

    if (options.signal?.aborted) {
      state.phase = 'cancelled';
      options.onProgress?.({ ...state });
      return { ...state };
    }

    state.phase = 'finishing';
    options.onProgress?.({ ...state });

    // "Soft" is relative to this shoot, so it can only be decided once every
    // frame has a score. Recomputed wholesale each time, which keeps the flags
    // correct when frames are added to a shoot later.
    //
    // Decided before the grouping, and handed to it, so the two markers agree:
    // nothing this pass calls soft is also suggested as the frame to keep.
    const soft = softFrames(this.assist.scoresFor(projectId));
    this.assist.setSoft(projectId, soft);
    this.group(projectId, soft);
    this.sink.touch();

    state.phase = 'done';
    options.onProgress?.({ ...state });
    return { ...state };
  }

  /**
   * Give every frame a vector.
   *
   * Deliberately a second pass rather than a second job inside the first. The
   * sharpness pass is cheap and finishes in seconds; embedding is minutes and
   * needs a download first. Running them in sequence means the amber dots appear
   * while the model is still arriving, instead of nothing appearing until both
   * are done.
   *
   * A failure here is not a failure of the review. If the model cannot be
   * fetched — an offline laptop, a blocked CDN — the sharpness flags stand on
   * their own and the reason is reported rather than thrown.
   */
  private async embed(projectId: number, state: AssistProgress, options: ReviewOptions): Promise<void> {
    const embedder = this.embedder;
    if (!embedder) return;

    const queue = this.assist.needingEmbedding(projectId, embedder.model, options.batch ?? 100_000);
    if (queue.length === 0) return;

    state.phase = 'loading';
    options.onProgress?.({ ...state });

    try {
      const status = await embedder.warm((download) => {
        state.download = download;
        options.onProgress?.({ ...state });
      });
      state.device = status.device;
      state.download = undefined;
    } catch (error) {
      state.error = error instanceof Error ? error.message : String(error);
      options.onProgress?.({ ...state });
      return;
    }

    state.phase = 'grouping';
    state.done = 0;
    state.total = queue.length;
    options.onProgress?.({ ...state });

    let embedded = 0;
    let lastError: string | null = null;

    for (let i = 0; i < queue.length; i++) {
      if (options.signal?.aborted) {
        state.phase = 'cancelled';
        options.onProgress?.({ ...state });
        return;
      }

      const mediaId = queue[i];
      try {
        const bytes = await this.thumbnailBytes(mediaId);
        if (bytes) {
          this.assist.putEmbedding(mediaId, await embedder.embed(bytes), embedder.model);
          state.done++;
          embedded++;
        } else {
          state.failed++;
          lastError ??= 'no thumbnail could be read';
        }
      } catch (error) {
        state.failed++;
        lastError = error instanceof Error ? error.message : String(error);
        // One undecodable frame is a skipped frame. A dead worker is the end of
        // the pass, and pretending otherwise would spin through every remaining
        // photograph collecting the same error.
        if (!(error instanceof Error) || !/worker|stopped/i.test(error.message)) continue;
        state.error = error.message;
        return;
      }

      if (i % CHUNK === CHUNK - 1) {
        this.sink.touch();
        options.onProgress?.({ ...state });
        await yieldToUi();
      }
    }

    this.sink.touch();

    // A pass where every single frame failed is a failed pass, whatever the
    // individual errors were. Reporting 'done' for it would leave the interface
    // cheerfully claiming a review that produced nothing, which is the worst of
    // both outcomes: no bursts, and no idea why.
    if (embedded === 0 && lastError) state.error = lastError;
  }

  /**
   * Measure focus properly, from the originals.
   *
   * The screening pass reads the 240px thumbnail, which is free and instant and
   * — measured, not assumed — separates a sharp frame from a blurred one by
   * about nine per cent. Nine per cent is a real signal, but it is thin enough
   * that on a real shoot it chose a different frame than a 1024px reading in
   * roughly half of all bursts. This pass goes back to the original for the
   * number the badge is actually decided on.
   *
   * Every frame, not only the ones in a burst: the pick floor is a percentile of
   * the shoot, so it needs the shoot, and a floor computed from burst members
   * alone would be dragged down by exactly the soft bursts it exists to refuse.
   * It also gives the sharpest-first sort a number worth sorting on.
   *
   * Resumable and chunked like the others, and a failure here is not a failure
   * of the review: without it, picks fall back to the screening number and the
   * result is what shipped before, not nothing.
   */
  private async measureFocus(
    projectId: number,
    state: AssistProgress,
    options: ReviewOptions,
  ): Promise<void> {
    const read = this.focusPixels;
    if (!read) return;

    const queue = this.assist.needingFocus(projectId, FOCUS_TAG, options.batch ?? 100_000);
    if (queue.length === 0) return;

    state.phase = 'measuring';
    state.done = 0;
    state.total = queue.length;
    options.onProgress?.({ ...state });

    let measured = 0;

    for (let i = 0; i < queue.length; i++) {
      if (options.signal?.aborted) {
        state.phase = 'cancelled';
        options.onProgress?.({ ...state });
        return;
      }

      try {
        const pixels = await read(queue[i], FOCUS_PX);
        if (pixels) {
          this.assist.putFocus(queue[i], laplacianVariance(pixels), FOCUS_TAG, this.clock());
          state.done++;
          measured++;
        } else {
          state.failed++;
        }
      } catch {
        state.failed++;
      }

      // Nothing readable in a whole chunk and nothing readable so far: the
      // folder is gone. Walking the rest to fail identically on each would cost
      // minutes and change nothing.
      if (measured === 0 && state.failed >= CHUNK) {
        state.error = 'the photographs could not be re-read, so focus was not measured';
        return;
      }

      if (i % CHUNK === CHUNK - 1) {
        this.sink.touch();
        options.onProgress?.({ ...state });
        await yieldToUi();
      }
    }

    this.sink.touch();
  }

  /**
   * Turn the stored vectors into bursts, and each burst into its suggestions.
   *
   * Deliberately reads the vectors back out of the database rather than keeping
   * them from the pass that made them. It means changing a threshold, or adding
   * frames to a shoot, regroups everything without running the model again —
   * which is the entire reason the vectors are stored.
   *
   * Does nothing at all when there are no vectors. A shoot that only had its
   * sharpness measured keeps its amber dots and gains no stars, rather than
   * being told every frame is its own burst.
   */
  private group(projectId: number, soft: ReadonlySet<number>): void {
    if (!this.embedder) return;

    const framed = this.assist.embeddingsFor(projectId, this.embedder.model);
    if (framed.length === 0) return;

    const bursts = burstsOf(framed);

    // Judged on focus where it exists, and on the screening number only where it
    // does not. Never a mixture inside one comparison: the two are measured at
    // different sizes and are not on the same scale, so ranking a burst with one
    // of each would be worse than ranking it with the poorer of the two.
    const focus = this.assist.focusFor(projectId, FOCUS_TAG);
    const shoot = focus.length > 0 ? focus : this.assist.scoresFor(projectId);
    const sharpness = new Map(shoot.map((s) => [s.id, s.sharpness]));
    const floor = pickFloor(shoot);

    const picks = new Set<number>();
    for (const group of groupByBurst(bursts).values()) {
      const scored = group
        .filter((id) => sharpness.has(id))
        .map((id) => ({ id, sharpness: sharpness.get(id)! }));
      for (const id of picksForBurst(scored, { soft, floor })) picks.add(id);
    }

    this.assist.setBurstsAndPicks(projectId, bursts, picks);
  }

  /** Forget everything for a shoot, so it can be reviewed from scratch. */
  reset(projectId: number): void {
    this.assist.clear(projectId);
    this.sink.touch();
  }
}

/** Hand the main thread back to the browser for a frame. */
function yieldToUi(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}
